import React, { useState } from 'react';
import { DollarSign, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface WalletTestPanelProps {
  onSuccess?: () => void;
}

const WalletTestPanel: React.FC<WalletTestPanelProps> = ({ onSuccess }) => {
  const { user } = useAuth();
  const [amount, setAmount] = useState<string>('10');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleTestWallet = async () => {
    if (!user) {
      setError('You must be logged in to test wallet funding');
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wallet-webhook-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          userId: user.id,
          amount: parsedAmount
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to test wallet funding');
      }

      const data = await response.json();
      console.log('Wallet test response:', data);
      
      setSuccess(`Successfully added $${parsedAmount} to your wallet!`);
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Refresh the page after a delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (err) {
      console.error('Error testing wallet:', err);
      setError(err instanceof Error ? err.message : 'Failed to test wallet funding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4">Test Wallet Funding</h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 rounded-lg flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-3 bg-green-50 rounded-lg flex items-center text-green-700">
          <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Amount to Add ($)
        </label>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="number"
            min="1"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
      </div>
      
      <button
        onClick={handleTestWallet}
        disabled={loading}
        className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <DollarSign className="w-5 h-5 mr-2" />
            Test Add Funds
          </>
        )}
      </button>
      
      <p className="mt-4 text-xs text-gray-500 text-center">
        This is for testing only. It will directly add funds to your wallet without processing a real payment.
      </p>
    </div>
  );
};

export default WalletTestPanel;